import { useState, useMemo, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Chart from "react-apexcharts";
import { formatLocalDate } from "../../../utils/formatDate";
import { CustomTable } from "../../../componentes/table/CustomTable";
import FortesFiscalService from "../../../services/resource/fortesFiscalService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import Button from "../../../componentes/button/button";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const n = (v) => v != null ? Number(v) : 0;
const fmt = (v, d = 2) => n(v).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtD = (v) => formatLocalDate(v);

// ─── Paleta ───────────────────────────────────────────────────────────────────
const C = {
    teal: "#0d9488", tealMid: "#14b8a6",
    blue: "#2563eb", blueLight: "#93c5fd",
    rose: "#f43f5e", amber: "#f59e0b",
    violet: "#7c3aed", green: "#16a34a",
    orange: "#ea580c", slate: "#475569",
};
const PALETTE = [C.teal, C.blue, C.rose, C.amber, C.violet, C.green, C.orange, "#06b6d4", "#ec4899", "#84cc16"];

const TABS = [
    { id: "lista", label: "Eventos", icon: "fa fa-list" },
    { id: "relatorios", label: "Relatórios", icon: "fa fa-bar-chart" },
];

// ─── Chart defaults ───────────────────────────────────────────────────────────
const baseChart = { toolbar: { show: false }, fontFamily: "'DM Mono', monospace", animations: { enabled: true, speed: 500 } };
const baseTip = { style: { fontFamily: "'DM Mono', monospace", fontSize: "11px" } };
const baseGrid = { borderColor: "#f1f5f9" };
const lblStyle = { style: { fontSize: "10px", colors: C.slate } };

// ─── Sub-components ───────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, accent = "teal" }) {
    const map = {
        teal: { bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-700", bar: "bg-teal-500" },
        blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", bar: "bg-blue-500" },
        violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", bar: "bg-violet-500" },
        amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", bar: "bg-amber-400" },
        rose: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", bar: "bg-rose-500" },
        green: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", bar: "bg-green-500" },
        orange: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", bar: "bg-orange-500" },
    };
    const s = map[accent] ?? map.teal;
    return (
        <div className={`rounded-xl border ${s.border} ${s.bg} p-3 flex items-start gap-3 relative overflow-hidden`}>
            <div className={`absolute top-0 left-0 w-[3px] h-full ${s.bar}`} />
            <i className={`${icon} ${s.text} text-lg mt-0.5 w-5 text-center flex-shrink-0 opacity-70`} />
            <div className="min-w-0">
                <p className={`text-[10px] font-semibold uppercase tracking-widest opacity-60 ${s.text}`}>{label}</p>
                <p className={`text-lg font-black leading-tight ${s.text}`}>{value}</p>
                {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

function SectionHeader({ icon, title, sub, color = "teal" }) {
    const c = { teal: "text-teal-600", blue: "text-blue-600", rose: "text-rose-600", amber: "text-amber-600", violet: "text-violet-600" };
    return (
        <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-card shadow-xs border border-border">
                <i className={`${icon} ${c[color] ?? c.teal} text-sm`} />
            </div>
            <div>
                <h2 className="text-sm font-display font-bold text-foreground leading-tight">{title}</h2>
                {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
            </div>
        </div>
    );
}

function Card({ children, className = "" }) {
    return <div className={`surface-card p-4 ${className}`}>{children}</div>;
}

function ChartTitle({ children }) {
    return <p className="text-xs font-semibold text-muted-foreground mb-3">{children}</p>;
}

function EventoTagInput({ value, onChange, eventosDisponiveis = [] }) {
    const [input, setInput] = useState("");
    const [open, setOpen] = useState(false);
    const [filtro, setFiltro] = useState("");

    const add = () => {
        const trimmed = input.trim().toUpperCase();
        if (trimmed && !value.includes(trimmed)) {
            onChange([...value, trimmed]);
        }
        setInput("");
    };

    const remove = (tag) => onChange(value.filter(t => t !== tag));

    const handleKey = (e) => {
        if (["Enter", ",", " "].includes(e.key)) { e.preventDefault(); add(); }
        if (e.key === "Backspace" && !input && value.length) remove(value[value.length - 1]);
    };

    const toggleEvento = (codigo) => {
        if (value.includes(codigo)) {
            remove(codigo);
        } else {
            onChange([...value, codigo]);
        }
    };

    const eventosFiltrados = eventosDisponiveis.filter(ev =>
        ev.codigo?.toString().toLowerCase().includes(filtro.toLowerCase()) ||
        ev.nome?.toLowerCase().includes(filtro.toLowerCase())
    );

    return (
        <>
            <div
                className="flex flex-wrap gap-1.5 items-center border border-input rounded-lg px-2 py-1.5 bg-card min-h-[38px] focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary transition-all cursor-text"
                onClick={() => document.getElementById("evento-input")?.focus()}
            >
                {value.map(tag => (
                    <span
                        key={tag}
                        className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-teal-200"
                    >
                        {tag}
                        <button
                            onClick={() => remove(tag)}
                            className="text-teal-400 hover:text-teal-700 transition-colors leading-none"
                        >
                            ×
                        </button>
                    </span>
                ))}

                <input
                    id="evento-input"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    onBlur={add}
                    placeholder={value.length === 0 ? "Ex: 001, Enter para adicionar…" : ""}
                    className="flex-1 min-w-[120px] text-xs text-slate-700 border-0 outline-none bg-transparent"
                />

                {value?.length != 0 &&
                    <button
                        type="button"
                        onClick={() => onChange([])}
                        className="ml-auto text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded"
                    >
                        Remover tudo
                    </button>
                }
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="ml-auto text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded"
                >
                    Selecionar
                </button>
            </div>

            <Dialog
                header="Selecionar Eventos"
                visible={open}
                onHide={() => setOpen(false)}
                className="min-h-[95vh] min-w-[70%]"
                blockScroll={true}
            >
                <div className="flex flex-col gap-3">

                    {/* Input busca padronizado */}
                    <input
                        type="text"
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        placeholder="Buscar por código ou nome..."
                        className="border border-input rounded-lg px-3 py-2 mt-3 text-sm text-foreground
                                   focus:outline-none focus:ring-2 focus:ring-primary/40
                                   focus:border-primary transition-all bg-card"
                    />

                    {/* Lista */}
                    <div className="flex flex-col gap-2 max-h-[53vh] overflow-auto border border-slate-100 rounded-lg p-2">
                        {eventosFiltrados.map(ev => (
                            <label
                                key={ev.codigo}
                                className="flex items-center gap-2 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                            >
                                <Checkbox
                                    inputId={ev.codigo}
                                    checked={value.includes(ev.codigo)}
                                    onChange={() => toggleEvento(ev.codigo)}
                                />
                                <span className="text-sm text-slate-700">
                                    <span className="font-semibold">{ev.codigo}</span> - {ev.nome}
                                </span>
                            </label>
                        ))}

                        {eventosFiltrados.length === 0 && (
                            <span className="text-xs text-slate-400 text-center py-3">
                                Nenhum evento encontrado
                            </span>
                        )}
                    </div>

                    {/* Botões padronizados */}
                    <div className="flex justify-end gap-2 pt-2 border-t">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="px-3 py-2 text-sm rounded-lg border border-slate-200 
                                       hover:bg-slate-50 transition-all"
                        >
                            Cancelar
                        </button>

                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="px-3 py-2 text-sm rounded-lg bg-teal-600 
                                       hover:bg-teal-700 text-white font-semibold 
                                       shadow-xs hover:shadow-card transition-all"
                        >
                            Confirmar
                        </button>
                    </div>
                </div>
            </Dialog>
        </>
    );
}

function exportListaEventosPDF(data, anoAndMes) {
    const doc = new jsPDF({ orientation: "landscape" });

    const headers = [
        "Código",
        "Nome",
        "Ano/Mês",
        "Evento",
        "Base Cálculo",
        "Referência",
        "Valor",
        "Dt. Cálculo",
        "Admissão",
        "Contrato",
    ];

    const rows = data.map(r => [
        r.codigo,
        r.nome,
        r.anoMes,
        r.eveCodigo + '-' + r.eveNome,
        r.baseCalculo != null ? `R$ ${fmt(r.baseCalculo)}` : "-",
        r.atributoEve,
        r.valor != null ? `R$ ${fmt(r.valor)}` : "-",
        fmtD(r.dtCalculo),
        fmtD(r.admissaoData),
        r.contrato
    ]);

    doc.setFontSize(12);
    doc.text("Eventos de Folha " + anoAndMes, 14, 15);

    autoTable(doc, {
        startY: 20,
        head: [headers],
        body: rows,
        styles: {
            fontSize: 8
        },
        headStyles: {
            fillColor: [41, 128, 185]
        }
    });

    // totais
    const totalValor = data.reduce((a, r) => a + n(r.valor), 0);
    const totalRef = data.reduce((a, r) => a + n(r.referencia), 0);
    const totalBase = data.reduce((a, r) => a + n(r.baseCalculo), 0);

    const finalY = doc.lastAutoTable.finalY + 5;

    doc.setFontSize(9);
    doc.text(`Registros: ${data.length}`, 14, finalY);
    doc.text(`Referência: ${fmt(totalRef)}`, 60, finalY);
    doc.text(`Base Cálculo: R$ ${fmt(totalBase)}`, 120, finalY);
    doc.text(`Total Valor: R$ ${fmt(totalValor)}`, 200, finalY);

    doc.save("Eventos-de-Folha-" + anoAndMes + ".pdf");
}

// ─── Aba Lista ────────────────────────────────────────────────────────────────
function ListaEventos({ data, loading, anoAndMes }) {
    const columns = useMemo(() => [
        {
            key: "nomeEmpresa",
            label: "Empresa",
            sortable: true,
            className: "bg-blue-50 text-left",
        },

        {
            key: "codigo",
            label: "Código",
            sortable: true,
            className: "bg-blue-50 text-center",
        },

        {
            key: "nome",
            label: "Nome",
            sortable: true,
            className: "bg-blue-50",
        },
        {
            key: "anoMes",
            label: "Ano/Mês",
            sortable: true,
            center: true,
            className: "bg-slate-50",
        },
        {
            key: "eveCodigo",
            label: "Cód. Evento",
            sortable: true,
            center: true,
            className: "font-semibold",
        },
        {
            key: "eveNome",
            label: "Descrição Evento",
            sortable: true,
            className: "font-semibold",
        },
        {
            key: "baseCalculo",
            label: "Base Cálculo",
            sortable: true,
            center: true,
            render: (v) => v != null ? `R$ ${fmt(v)}` : "—",
        },
        {
            key: "atributoEve",
            label: "Referência",
            sortable: true,
            center: true,
        },
        {
            key: "valor",
            label: "Valor",
            sortable: true,
            center: true,
            className: "bg-green-50 font-black",
            render: (v) => v != null ? `R$ ${fmt(v)}` : "—",
        },

        {
            key: "dtCalculo",
            label: "Dt. Cálculo",
            sortable: true,
            center: true,
            render: (v) => fmtD(v),
        },
        {
            key: "admissaoData",
            label: "Admissão",
            sortable: true,
            center: true,
            render: (v) => fmtD(v),
        },
        {
            key: "contrato",
            label: "Contrato",
            sortable: true,
            center: true,
        }
    ], []);

    return (
        <CustomTable
            data={data}
            columns={columns}
            showHeader={false}
            loading={loading}
            exportable
            maxHeight={600}
            pagination
            pageSize={20}
            exportFilename={`Eventos de Folha ${anoAndMes}`}
            className="text-[11px]"
            footer={(rows) => {
                const totalValor = rows.reduce((a, r) => a + n(r.valor), 0);
                const totalRef = rows.reduce((a, r) => a + n(r.referencia), 0);
                const totalBase = rows.reduce((a, r) => a + n(r.baseCalculo), 0);
                return (
                    <div className="flex flex-wrap justify-end gap-5 p-2 text-xs font-semibold text-foreground">
                        <span>Registros: <b>{rows.length}</b></span>
                        <span className="text-teal-700">Referência: {fmt(totalRef)}</span>
                        <span className="text-amber-700">Base Cálculo: R$ {fmt(totalBase)}</span>
                        <span className="text-green-700">Total Valor: R$ {fmt(totalValor)}</span>
                    </div>
                );
            }}
        />
    );
}

// ─── Aba Relatórios ───────────────────────────────────────────────────────────
function RelatoriosEventos({ data }) {
    // ── Stats ──────────────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        if (!data.length) return null;
        const totalValor = data.reduce((a, r) => a + n(r.valor), 0);
        const totalRef = data.reduce((a, r) => a + n(r.referencia), 0);
        const totalBase = data.reduce((a, r) => a + n(r.baseCalculo), 0);
        const funcionarios = new Set(data.map(r => r.cpf).filter(Boolean)).size;
        const eventos = new Set(data.map(r => r.eveCodigo).filter(Boolean)).size;
        const lotes = new Set(data.map(r => r.lotCodigo).filter(Boolean)).size;
        return { totalValor, totalRef, totalBase, funcionarios, eventos, lotes, total: data.length };
    }, [data]);

    // ── 1. Valor por evento ────────────────────────────────────────────────────
    const chartValorEvento = useMemo(() => {
        const byEve = {};
        data.forEach(r => {
            const k = `${r.eveCodigo} – ${r.nome ?? ""}`.trim();
            if (!byEve[k]) byEve[k] = 0;
            byEve[k] += n(r.valor);
        });
        const sorted = Object.entries(byEve).sort((a, b) => b[1] - a[1]).slice(0, 12);
        const cats = sorted.map(([k]) => k.length > 30 ? k.slice(0, 30) + "…" : k);
        return {
            options: {
                chart: { ...baseChart, type: "bar" },
                plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "60%" } },
                colors: [C.teal],
                dataLabels: { enabled: false },
                xaxis: { categories: cats, labels: { ...lblStyle } },
                yaxis: { labels: { style: { fontSize: "10px" }, maxWidth: 220 } },
                tooltip: { ...baseTip, y: { formatter: v => `R$ ${fmt(v)}` } },
                grid: baseGrid,
            },
            series: [{ name: "Valor Total", data: sorted.map(([, v]) => +v.toFixed(2)) }],
        };
    }, [data]);

    // ── 2. Referência por evento ───────────────────────────────────────────────
    const chartRefEvento = useMemo(() => {
        const byEve = {};
        data.forEach(r => {
            const k = `${r.eveCodigo} – ${r.nome ?? ""}`.trim();
            if (!byEve[k]) byEve[k] = 0;
            byEve[k] += n(r.referencia);
        });
        const sorted = Object.entries(byEve).sort((a, b) => b[1] - a[1]).slice(0, 12);
        const cats = sorted.map(([k]) => k.length > 30 ? k.slice(0, 30) + "…" : k);
        return {
            options: {
                chart: { ...baseChart, type: "bar" },
                plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "60%" } },
                colors: [C.blue],
                dataLabels: { enabled: false },
                xaxis: { categories: cats, labels: { ...lblStyle } },
                yaxis: { labels: { style: { fontSize: "10px" }, maxWidth: 220 } },
                tooltip: { ...baseTip, y: { formatter: v => fmt(v) } },
                grid: baseGrid,
            },
            series: [{ name: "Referência", data: sorted.map(([, v]) => +v.toFixed(2)) }],
        };
    }, [data]);

    // ── 3. Valor por funcionário (top 15) ─────────────────────────────────────
    const chartValorFunc = useMemo(() => {
        const byFunc = {};
        data.forEach(r => {
            const k = r.nome ?? r.cpf ?? "—";
            if (!byFunc[k]) byFunc[k] = 0;
            byFunc[k] += n(r.valor);
        });
        const sorted = Object.entries(byFunc).sort((a, b) => b[1] - a[1]).slice(0, 15);
        const cats = sorted.map(([k]) => k.length > 25 ? k.slice(0, 25) + "…" : k);
        return {
            options: {
                chart: { ...baseChart, type: "bar" },
                plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "60%" } },
                colors: [C.violet],
                dataLabels: { enabled: false },
                xaxis: { categories: cats, labels: { ...lblStyle } },
                yaxis: { labels: { style: { fontSize: "10px" }, maxWidth: 200 } },
                tooltip: { ...baseTip, y: { formatter: v => `R$ ${fmt(v)}` } },
                grid: baseGrid,
            },
            series: [{ name: "Valor Total", data: sorted.map(([, v]) => +v.toFixed(2)) }],
        };
    }, [data]);

    // ── 4. Distribuição de valor por evento (donut) ────────────────────────────
    const chartDonut = useMemo(() => {
        const byEve = {};
        data.forEach(r => {
            const k = r.eveCodigo ?? "—";
            if (!byEve[k]) byEve[k] = 0;
            byEve[k] += n(r.valor);
        });
        const sorted = Object.entries(byEve).sort((a, b) => b[1] - a[1]).slice(0, 10);
        return {
            options: {
                chart: { ...baseChart, type: "donut" },
                labels: sorted.map(([k]) => k),
                colors: PALETTE,
                legend: { position: "bottom", fontSize: "10px" },
                dataLabels: { enabled: true, formatter: v => `${v.toFixed(1)}%`, style: { fontSize: "10px" } },
                tooltip: { ...baseTip, y: { formatter: v => `R$ ${fmt(v)}` } },
                plotOptions: {
                    pie: {
                        donut: {
                            size: "65%", labels: {
                                show: true,
                                total: {
                                    show: true, label: "Total", fontSize: "11px",
                                    formatter: () => `R$ ${fmt(sorted.reduce((a, [, v]) => a + v, 0), 0)}`
                                },
                            }
                        }
                    }
                },
            },
            series: sorted.map(([, v]) => +v.toFixed(2)),
        };
    }, [data]);

    // ── 5. Ocorrências por funcionário (qtd registros) ─────────────────────────
    const chartOcorrencias = useMemo(() => {
        const byFunc = {};
        data.forEach(r => {
            const k = r.nome ?? r.cpf ?? "—";
            if (!byFunc[k]) byFunc[k] = 0;
            byFunc[k]++;
        });
        const sorted = Object.entries(byFunc).sort((a, b) => b[1] - a[1]).slice(0, 15);
        const cats = sorted.map(([k]) => k.length > 25 ? k.slice(0, 25) + "…" : k);
        return {
            options: {
                chart: { ...baseChart, type: "bar" },
                plotOptions: { bar: { borderRadius: 4, columnWidth: "65%" } },
                colors: [C.amber],
                dataLabels: { enabled: false },
                xaxis: { categories: cats, labels: { rotate: -35, ...lblStyle } },
                yaxis: { labels: { formatter: v => String(Math.round(v)), ...lblStyle } },
                tooltip: { ...baseTip, y: { formatter: v => `${v} registro(s)` } },
                grid: baseGrid,
            },
            series: [{ name: "Qtd. Eventos", data: sorted.map(([, v]) => v) }],
        };
    }, [data]);

    // ── 6. Valor vs Base cálculo por evento ───────────────────────────────────
    const chartValorVsBase = useMemo(() => {
        const byEve = {};
        data.forEach(r => {
            const k = r.eveCodigo ?? "—";
            if (!byEve[k]) byEve[k] = { valor: 0, base: 0 };
            byEve[k].valor += n(r.valor);
            byEve[k].base += n(r.baseCalculo);
        });
        const sorted = Object.entries(byEve).sort((a, b) => b[1].valor - a[1].valor).slice(0, 10);
        return {
            options: {
                chart: { ...baseChart, type: "bar" },
                plotOptions: { bar: { borderRadius: 4, columnWidth: "70%" } },
                colors: [C.green, C.amber],
                dataLabels: { enabled: false },
                xaxis: { categories: sorted.map(([k]) => k), labels: { ...lblStyle } },
                yaxis: { labels: { formatter: v => `R$ ${fmt(v, 0)}`, ...lblStyle } },
                legend: { position: "top", fontSize: "11px" },
                tooltip: { ...baseTip, y: { formatter: v => `R$ ${fmt(v)}` } },
                grid: baseGrid,
            },
            series: [
                { name: "Valor", data: sorted.map(([, v]) => +v.valor.toFixed(2)) },
                { name: "Base Cálculo", data: sorted.map(([, v]) => +v.base.toFixed(2)) },
            ],
        };
    }, [data]);

    // ── Tabela resumo por evento ───────────────────────────────────────────────
    const resumoEventos = useMemo(() => {
        const byEve = {};
        data.forEach(r => {
            const k = r.eveCodigo ?? "—";
            if (!byEve[k]) byEve[k] = { codigo: k, nome: r.eveNome ?? "—", valor: 0, referencia: 0, base: 0, count: 0 };
            byEve[k].valor += n(r.valor);
            byEve[k].referencia += n(r.referencia);
            byEve[k].base += n(r.baseCalculo);
            byEve[k].count++;
        });
        return Object.values(byEve).sort((a, b) => b.valor - a.valor);
    }, [data]);

    if (!data.length) return (
        <div className="flex flex-col items-center justify-center py-32 text-slate-300">
            <i className="fa fa-bar-chart text-6xl mb-4" />
            <p className="text-sm">Pesquise eventos para visualizar os relatórios.</p>
        </div>
    );

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 space-y-6">

            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7 gap-3">
                <KpiCard icon="fa fa-list-alt" label="Registros" value={stats.total} accent="blue" />
                <KpiCard icon="fa fa-users" label="Funcionários" value={stats.funcionarios} accent="teal" />
                <KpiCard icon="fa fa-tag" label="Eventos Únicos" value={stats.eventos} accent="violet" />
                <KpiCard icon="fa fa-th" label="Lotes" value={stats.lotes} accent="orange" />
                <KpiCard icon="fa fa-clock-o" label="Referência" value={fmt(stats.totalRef, 0)} accent="amber" />
                <KpiCard icon="fa fa-calculator" label="Base Cálculo" value={`R$ ${fmt(stats.totalBase, 0)}`} accent="rose" />
                <KpiCard icon="fa fa-dollar" label="Total Valor" value={`R$ ${fmt(stats.totalValor, 0)}`} accent="green" />
            </div>

            {/* Seção 1: Valor por evento */}
            <section>
                <SectionHeader icon="fa fa-tag" title="Análise por Evento" sub="Valor, referência e base de cálculo consolidados" color="teal" />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Card>
                        <ChartTitle>Top 12 — Valor Total por Evento</ChartTitle>
                        <Chart type="bar" options={chartValorEvento.options} series={chartValorEvento.series} height={300} />
                    </Card>
                    <Card>
                        <ChartTitle>Top 12 — Referência por Evento</ChartTitle>
                        <Chart type="bar" options={chartRefEvento.options} series={chartRefEvento.series} height={300} />
                    </Card>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
                    <Card>
                        <ChartTitle>Distribuição de Valor por Evento</ChartTitle>
                        <Chart type="donut" options={chartDonut.options} series={chartDonut.series} height={300} />
                    </Card>
                    <Card>
                        <ChartTitle>Valor vs Base de Cálculo por Evento</ChartTitle>
                        <Chart type="bar" options={chartValorVsBase.options} series={chartValorVsBase.series} height={300} />
                    </Card>
                </div>
            </section>

            {/* Seção 2: Por funcionário */}
            <section>
                <SectionHeader icon="fa fa-users" title="Análise por Funcionário" sub="Distribuição de valor e quantidade de eventos" color="violet" />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Card>
                        <ChartTitle>Top 15 — Valor Total por Funcionário</ChartTitle>
                        <Chart type="bar" options={chartValorFunc.options} series={chartValorFunc.series} height={320} />
                    </Card>
                    <Card>
                        <ChartTitle>Top 15 — Quantidade de Eventos por Funcionário</ChartTitle>
                        <Chart type="bar" options={chartOcorrencias.options} series={chartOcorrencias.series} height={320} />
                    </Card>
                </div>
            </section>

            {/* Seção 3: Tabela resumo */}
            <section>
                <SectionHeader icon="fa fa-table" title="Resumo por Evento" sub="Consolidado de todos os eventos da pesquisa" color="amber" />
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[11px] text-foreground">
                            <thead>
                                <tr className="border-b border-border text-muted-foreground font-semibold">
                                    <th className="text-left py-2 px-2">#</th>
                                    <th className="text-left py-2 px-2">Código</th>
                                    <th className="text-left py-2 px-2">Descrição</th>
                                    <th className="text-right py-2 px-2">Qtd.</th>
                                    <th className="text-right py-2 px-2">Referência</th>
                                    <th className="text-right py-2 px-2">Base Cálculo</th>
                                    <th className="text-right py-2 px-2">Valor Total</th>
                                    <th className="text-right py-2 px-2">Média / Reg.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resumoEventos.map((ev, i) => (
                                    <tr key={ev.codigo} className="border-b border-border hover:bg-table-row-hover transition-colors">
                                        <td className="py-2 px-2 text-muted-foreground">{i + 1}</td>
                                        <td className="py-2 px-2 font-mono font-semibold text-primary">{ev.codigo}</td>
                                        <td className="py-2 px-2 font-semibold text-foreground">{ev.nome}</td>
                                        <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">{ev.count}</td>
                                        <td className="py-2 px-2 text-right tabular-nums text-teal-700 font-semibold">{fmt(ev.referencia)}</td>
                                        <td className="py-2 px-2 text-right tabular-nums text-amber-700">{ev.base > 0 ? `R$ ${fmt(ev.base)}` : "—"}</td>
                                        <td className="py-2 px-2 text-right tabular-nums text-green-700 font-bold">R$ {fmt(ev.valor)}</td>
                                        <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">R$ {fmt(ev.valor / ev.count)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-border bg-muted font-bold text-foreground">
                                    <td colSpan={3} className="py-2 px-2">TOTAL</td>
                                    <td className="py-2 px-2 text-right tabular-nums">{resumoEventos.reduce((a, e) => a + e.count, 0)}</td>
                                    <td className="py-2 px-2 text-right tabular-nums text-teal-700">{fmt(stats.totalRef)}</td>
                                    <td className="py-2 px-2 text-right tabular-nums text-amber-700">R$ {fmt(stats.totalBase)}</td>
                                    <td className="py-2 px-2 text-right tabular-nums text-green-700">R$ {fmt(stats.totalValor)}</td>
                                    <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">—</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </Card>
            </section>
        </motion.div>
    );
}

// ════════════════════════════════════════════════════════════════════════════════
export default function EventoFolha() {
    const fortesService = useMemo(() => new FortesFiscalService(), []);

    // ── Filtros ───────────────────────────────────────────────────────────────

    const [empresaCodigo, setEmpresaCodigo] = useState('');
    const [estabelecimentoCodigo, setEstabelecimentoCodigo] = useState('');
    const [eventos, setEventos] = useState([]);
    const [anoMes, setAnoMes] = useState("");

    const [activeTab, setActiveTab] = useState("lista");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState(null);
    const [eventosDisponiveis, setEventosDisponiveis] = useState([])

    const STORAGE_KEY = 'filtros-eventos-folha';

    useEffect(() => {
        try {
            const savedFilters = localStorage.getItem(STORAGE_KEY);

            if (!savedFilters) return;

            const parsed = JSON.parse(savedFilters);

            // validação básica para evitar dados corrompidos
            if (typeof parsed === 'object' && parsed !== null) {
                setEmpresaCodigo(parsed.empresaCodigo ?? '');
                setEstabelecimentoCodigo(parsed.estabelecimentoCodigo ?? '');
                setEventos(Array.isArray(parsed.eventos) ? parsed.eventos : []);
                setAnoMes(parsed.anoMes ?? undefined);
            }
        } catch (error) {
            console.error('Erro ao recuperar filtros do localStorage', error);
            localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    useEffect(() => {
        fortesService.buscarEventos(empresaCodigo).then(res => {
            setEventosDisponiveis(res.data)
        })

        if (empresaCodigo !== "0184") setEstabelecimentoCodigo("");
    }, [empresaCodigo])

    useEffect(() => {
        const filtros = {
            empresaCodigo,
            estabelecimentoCodigo,
            eventos,
            anoMes,
        };

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(filtros));
        } catch (error) {
            console.error('Erro ao salvar filtros no localStorage', error);
        }
    }, [empresaCodigo, estabelecimentoCodigo, eventos, anoMes]);

    const canSearch = anoMes.length === 6;

    const buscar = useCallback(() => {
        if (!canSearch) return;
        setLoading(true);
        setError(null);
        fortesService.buscarEventosFolha({
            empresaCodigo: empresaCodigo.trim(),
            estabelecimentoCodigo: estabelecimentoCodigo.trim(),
            eventos,
            anoMes,
        })
            .then(res => { setData(res.data); setSearched(true); })
            .catch(e => { console.error(e); setError("Erro ao buscar eventos. Verifique os parâmetros."); })
            .finally(() => setLoading(false));
    }, [empresaCodigo, estabelecimentoCodigo, eventos, anoMes]);

    const handleKeyDown = (e) => { if (e.key === "Enter") buscar(); };

    return (
        <div className="bg-background min-h-screen">

            {/* ── Header ── */}
            <div className="bg-card border-b border-border px-4 pt-4">
                <div className="mb-4">
                    <h1 className="text-xl font-display font-bold text-foreground">Eventos de Folha</h1>
                    <p className="text-sm text-muted-foreground">Consulta de eventos via integração Fortes AC</p>
                </div>

                {/* Filtros */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-8 gap-3 mb-4">
                    {/* Empresa */}

                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Empresa</label>
                        <select
                            type="text"
                            value={empresaCodigo}
                            onChange={e => setEmpresaCodigo(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Cód. empresa"
                            className="border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all bg-card"
                        >
                            <option value="">Todas</option>
                            <option value="0184">York Logística</option>
                            <option value="0182">York Caxias</option>
                            <option value="0164">York Corporativo</option>
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Estabelecimento</label>
                        <select
                            type="text"
                            value={estabelecimentoCodigo}
                            onChange={e => setEstabelecimentoCodigo(e.target.value)}
                            disabled={empresaCodigo !== "0184"}
                            onKeyDown={handleKeyDown}
                            placeholder="Cód. estabelecimento"
                            className="border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all bg-card"
                        >
                            <option value="">Todas</option>
                            <option value="0001">YL Piripiri</option>
                            <option value="0002">YL Floriano</option>
                            <option value="0003">YL Picos</option>
                        </select>
                    </div>

                    {/* Ano/Mês */}
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Competência <span className="text-slate-400 normal-case"></span></label>
                        <input
                            type="text"
                            value={anoMes}
                            onChange={e => setAnoMes(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            onKeyDown={handleKeyDown}
                            placeholder="Ex: 202603"
                            maxLength={6}
                            className="border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all bg-card font-mono"
                        />
                    </div>

                    {/* Eventos (tag input) */}
                    <div className="flex flex-col gap-1 col-span-3">
                        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                            Eventos <span className="text-slate-400 normal-case">({eventos.length} selecionado{eventos.length !== 1 ? "s" : ""})</span>
                        </label>
                        <EventoTagInput value={eventos} onChange={setEventos} eventosDisponiveis={eventosDisponiveis} />
                    </div>

                    {/* Botão */}
                    <div className="col-span-1 flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-transparent uppercase tracking-widest select-none">Buscar</label>
                        <button
                            onClick={buscar}
                            disabled={!canSearch || loading}
                            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all
                                ${canSearch && !loading
                                    ? "bg-teal-600 hover:bg-teal-700 text-white shadow-xs hover:shadow-card"
                                    : "bg-muted text-muted-foreground cursor-not-allowed"}`}
                        >
                            {loading
                                ? <><i className="fa fa-spinner fa-spin" /> Buscando…</>
                                : <><i className="fa fa-search" /> Buscar</>}
                        </button>
                    </div>

                    <div className="col-span-1 flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-transparent uppercase tracking-widest select-none">Exportar</label>
                        <button
                            onClick={() => exportListaEventosPDF(data, anoMes)}
                            disabled={!canSearch}
                            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all
                                ${canSearch
                                    ? "bg-card border border-border hover:bg-muted text-foreground shadow-xs hover:shadow-card"
                                    : "bg-muted text-muted-foreground cursor-not-allowed"}`}
                        >
                            <i className="fa fa-file-pdf" /> Exportar
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-0 -mb-px">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition-all whitespace-nowrap
                                ${activeTab === tab.id
                                    ? "border-blue-600 text-blue-700 bg-blue-50/50"
                                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}
                        >
                            <i className={tab.icon} />
                            {tab.label}
                            {tab.id === "lista" && data.length > 0 && (
                                <span className="ml-1 bg-teal-100 text-teal-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                    {data.length}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Erro ── */}
            {error && (
                <div className="mx-4 mt-4 flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-700">
                    <i className="fa fa-exclamation-circle flex-shrink-0" />
                    {error}
                    <button onClick={() => setError(null)} className="ml-auto text-rose-400 hover:text-rose-700">×</button>
                </div>
            )}

            {/* ── Empty state ── */}
            {!searched && !loading && (
                <div className="flex flex-col items-center justify-center py-32 text-slate-300">
                    <i className="fa fa-search text-5xl mb-4" />
                    <p className="text-sm text-slate-400">Preencha os filtros e clique em <b>Buscar</b> para consultar eventos.</p>
                </div>
            )}

            {/* ── Tab Content ── */}
            {searched && (
                <AnimatePresence mode="wait">
                    {activeTab === "lista" && (
                        <motion.div key="lista" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <ListaEventos data={data} loading={loading} anoAndMes={anoMes} />
                        </motion.div>
                    )}
                    {activeTab === "relatorios" && (
                        <motion.div key="relatorios" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <RelatoriosEventos data={data} />
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </div>
    );
}
